import React, { FormEvent, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useChangePasswordMutation, useMeQuery } from "../api/authApi";
import { Eye, EyeOff } from "lucide-react";

const ChangePasswordPage: React.FC = () => {
  const navigate = useNavigate();
  const { refetch } = useMeQuery();
  const [changePassword, { isLoading, error }] = useChangePasswordMutation();

  const [currentPassword, setCurrentPassword] = useState("");
  const [password, setPassword] = useState("");

  // Visibility states
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);

  const [localError, setLocalError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const onSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLocalError(null);
    setSuccess(null);

    try {
      const res = await changePassword({
        current_password: currentPassword,
        password,
        // Sending the same password as confirmation since field was removed from UI
        password_confirmation: password,
      }).unwrap();

      setSuccess(res?.message || "Password changed successfully");

      // clear form
      setCurrentPassword("");
      setPassword("");

      // The mutation has already marked changed_password locally and invalidated
      // /me, so the onboarding guard releases this route on the next render. The
      // refetch is confirmation from the server, not the thing that frees the
      // member — it used to be both, which is why a slow /me left them here.
      await refetch();

      // No navigate() here on purpose. Where the member goes next depends on
      // what they still owe — an admin to /dashboard, a member to
      // /complete-profile — and OnboardingGuard already makes exactly that
      // decision once changed_password flips. Sending them to a fixed route
      // from here raced the guard and could only ever be right for one kind of
      // user; the guard releases this route on the next render.
    } catch (err: any) {
      setLocalError(
        err?.data?.message || err?.message || "Change password failed."
      );
    }
  };

  const getErrorMessage = () => {
    if (localError) return localError;
    if (!error) return null;

    if ("status" in error) {
      if (typeof error.status === "number") {
        if (error.status === 400) return "Invalid current password.";
        if (error.status === 401) return "Unauthorized. Please login again.";
        if (error.status === 422)
          return (error as any)?.data?.message || "Validation failed.";
        return `Server Error: ${error.status}`;
      }
      return "A connection error occurred.";
    }

    return (error as any)?.data?.message || "Change password failed";
  };

  const errorMessage = getErrorMessage();

  return (
    <div className="fixed inset-0 h-screen w-screen flex overflow-hidden bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-slate-900">
      <div className="flex-1 flex items-center justify-center px-4 py-10 lg:px-8">
        <div className="w-full max-w-md">
          <div className="bg-white/95 backdrop-blur rounded-2xl shadow-xl border border-slate-100 px-6 py-8 sm:px-8 sm:py-10">
            <div className="mb-6 text-center">
              <h2 className="text-xl font-semibold text-slate-900">
                Change Password
              </h2>
              <p className="mt-1 text-xs text-slate-500">
                You must update your password before continuing.
              </p>
            </div>

            <form onSubmit={onSubmit} className="space-y-5">
              {/* Current Password */}
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">
                  Current Password
                </label>
                <div className="relative">
                  <input
                    type={showCurrent ? "text" : "password"}
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    required
                    className="w-full px-3 py-2 text-sm border border-slate-200 rounded-md focus:ring-2 focus:ring-indigo-500 outline-none pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowCurrent(!showCurrent)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                  >
                    {showCurrent ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>

              {/* New Password */}
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">
                  New Password
                </label>
                <div className="relative">
                  <input
                    type={showNew ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="w-full px-3 py-2 text-sm border border-slate-200 rounded-md focus:ring-2 focus:ring-indigo-500 outline-none pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowNew(!showNew)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                  >
                    {showNew ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>

              {/* Status Messages */}
              {errorMessage && (
                <div className="text-xs font-semibold text-red-600 bg-red-50 border border-red-100 rounded-md px-3 py-2 animate-in fade-in duration-300">
                  {errorMessage}
                </div>
              )}

              {success && (
                <div className="text-xs font-semibold text-green-700 bg-green-50 border border-green-100 rounded-md px-3 py-2 animate-in fade-in duration-300">
                  {success}
                </div>
              )}

              {/* Submit */}
              <div className="space-y-3 pt-2">
                <button
                  disabled={isLoading}
                  type="submit"
                  className="w-full py-2.5 text-sm font-medium bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-60 transition-colors"
                >
                  {isLoading ? "Saving..." : "Change Password"}
                </button>

                <button
                  type="button"
                  onClick={() => navigate(-1)}
                  className="w-full py-2.5 text-sm font-medium bg-slate-100 text-slate-800 rounded-md hover:bg-slate-200 transition-colors"
                >
                  Back
                </button>
              </div>
            </form>

            <div className="mt-6 text-center text-[10px] uppercase tracking-wider text-slate-400">
              Security Protocol: NIQS Portal v2
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChangePasswordPage;
